import { useState } from 'react';
import { CalendarDays, Mail, MapPin, PackageCheck, ShieldCheck } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { api, recordFunnelEvent } from '../api.js';
import { AppShell } from '../components/AppShell.js';
import { Button, Notice } from '../components/ui.js';
import { copy } from '../i18n.js';
import { useDraft } from '../state/DraftContext.js';
import { createDraft, isDraftReadyForSummary } from '../state/draft.js';
import { PRIVACY_VERSION, TOS_VERSION } from '../../shared/legal.js';

export function SummaryPage() {
  const { draft, update, photo, setPhoto, session } = useDraft();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!draft || !session) return null;
  const t = copy[draft.language];
  const submit = async () => {
    if (!isDraftReadyForSummary(draft) || !draft.lostDate || !draft.place) return;
    setSubmitting(true);
    setError(null);
    const now = new Date();
    const submission = {
      schemaVersion: 'lost-item-report.v1',
      intake: {
        source: draft.source,
        language: draft.language,
        redactedTranscript: draft.transcript.length
          ? draft.transcript
          : [{ role: 'USER', text: draft.item.description }],
        confidence: draft.confidence,
        clarificationCount: draft.source === 'VOICE' ? draft.clarificationCount : 0,
        extractionToolCallCount: draft.source === 'VOICE' ? 1 : 0,
      },
      item: {
        description: draft.item.description.trim(),
        category: draft.item.category,
        color: draft.item.color,
        brand: draft.item.brand.trim() || 'UNKNOWN',
        ...(draft.item.distinguishingFeatures.trim()
          ? { distinguishingFeatures: draft.item.distinguishingFeatures.trim() }
          : {}),
      },
      lostDate: draft.lostDate,
      place: cleanPlace(draft.place),
      proof: draft.proofDescription.trim() ? { description: draft.proofDescription.trim() } : {},
      contact: {
        firstName: draft.contact.firstName.trim(),
        lastName: draft.contact.lastName.trim(),
        email: draft.contact.email.trim().toLowerCase(),
        ...(draft.contact.phone.trim() ? { phone: draft.contact.phone.trim() } : {}),
        preferredChannel: draft.contact.preferredChannel,
      },
      legal: {
        tosAccepted: true,
        tosVersion: TOS_VERSION,
        tosAcceptedAt: now.toISOString(),
        privacyAccepted: true,
        privacyNoticeVersion: PRIVACY_VERSION,
        privacyAcceptedAt: now.toISOString(),
      },
      funnel: {
        startedAt: draft.startedAt,
        submittedAt: now.toISOString(),
        totalDurationMs: Math.min(
          86_400_000,
          Math.max(0, now.getTime() - new Date(draft.startedAt).getTime()),
        ),
        ...(draft.voiceDurationMs !== undefined ? { voiceDurationMs: draft.voiceDurationMs } : {}),
        usedTextFallback: draft.usedTextFallback,
        mandatoryFieldCorrections: draft.mandatoryFieldCorrections,
      },
    };
    try {
      let result: {
        reportId: string;
        reference: string;
        trackingUrl: string;
        confirmationEmailState: 'PENDING' | 'ACCEPTED' | 'FAILED' | 'PREVIEW';
      };
      if (photo) {
        const form = new FormData();
        form.set('report', JSON.stringify(submission));
        form.set('photo', photo, photo.name);
        result = await api('/api/reports', { method: 'POST', body: form });
      } else {
        result = await api('/api/reports', { method: 'POST', body: JSON.stringify(submission) });
      }
      recordFunnelEvent({
        type: 'REPORT_SUBMITTED',
        step: 'CONFIRMATION',
        source: draft.source,
        language: draft.language,
      });
      const clean = createDraft({ ...session, language: draft.language });
      update({
        ...clean,
        reportId: result.reportId,
        reportReference: result.reference,
        trackingUrl: result.trackingUrl,
        confirmationEmailState: result.confirmationEmailState,
      });
      setPhoto(null);
      navigate('/report/confirmation', { replace: true });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell language={draft.language} step={5}>
      <header className="page-heading">
        <h1>{t.summary.title}</h1>
        <p>{t.summary.lead}</p>
      </header>
      {!isDraftReadyForSummary(draft) ? (
        <Notice tone="warning">Go back and complete the required information.</Notice>
      ) : null}
      <section className="summary-list">
        <SummaryRow
          icon={<PackageCheck />}
          title={t.summary.item}
          value={`${draft.item.description} · ${draft.item.category} · ${draft.item.color}`}
          edit="/report/intake"
          editLabel={t.summary.edit}
        />
        <SummaryRow
          icon={<CalendarDays />}
          title={t.summary.date}
          value={dateLabel(draft)}
          edit="/report/intake"
          editLabel={t.summary.edit}
        />
        <SummaryRow
          icon={<MapPin />}
          title={t.summary.place}
          value={placeLabel(draft)}
          edit="/report/place"
          editLabel={t.summary.edit}
        />
        <SummaryRow
          icon={<ShieldCheck />}
          title={t.summary.proof}
          value={draft.proofDescription || photo?.name || 'Not added'}
          edit="/report/proof"
          editLabel={t.summary.edit}
        />
        <SummaryRow
          icon={<Mail />}
          title={t.summary.contact}
          value={`${draft.contact.firstName} ${draft.contact.lastName} · ${draft.contact.email}`}
          edit="/report/contact"
          editLabel={t.summary.edit}
        />
      </section>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <div className="flow-actions">
        <Button variant="secondary" onClick={() => navigate('/report/contact')}>
          {t.common.back}
        </Button>
        <Button
          disabled={submitting || !isDraftReadyForSummary(draft)}
          onClick={() => void submit()}
        >
          {submitting ? t.summary.submitting : t.summary.submit}
        </Button>
      </div>
    </AppShell>
  );
}

function SummaryRow({
  icon,
  title,
  value,
  edit,
  editLabel,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  edit: string;
  editLabel: string;
}) {
  return (
    <article className="summary-row">
      <span className="summary-icon">{icon}</span>
      <div>
        <h2>{title}</h2>
        <p>{value}</p>
      </div>
      <Link to={edit}>{editLabel}</Link>
    </article>
  );
}

function dateLabel(draft: NonNullable<ReturnType<typeof useDraft>['draft']>) {
  return draft.lostDate?.kind === 'KNOWN' ? draft.lostDate.start : "I don't know";
}

function placeLabel(draft: NonNullable<ReturnType<typeof useDraft>['draft']>) {
  return !draft.place || draft.place.kind === 'UNKNOWN'
    ? "I don't know"
    : `${draft.place.name}${draft.place.context ? ` · ${draft.place.context}` : ''}`;
}

function cleanPlace(
  place: NonNullable<NonNullable<ReturnType<typeof useDraft>['draft']>['place']>,
) {
  if (place.kind === 'UNKNOWN') return place;
  const { context, ...rest } = place;
  return context?.trim() ? { ...rest, context: context.trim() } : rest;
}
